import { useEffect, useRef, useState } from 'react';
import { Crown } from 'lucide-react';

/**
 * WBTCrown — CrewZ v3 spec.
 *
 * Renders a tiny animated crown when a rolling metric meets/beats a World
 * Best Time target. Silent otherwise — no tooltips, no messages, no way
 * to see the record's value from this component (§8 no-record silence).
 *
 * Props:
 *   liveValue     — current instantaneous value (m/s on-water, s/500m ergo)
 *   target        — WBT target (m/s on-water, s/500m ergo). null → silent.
 *   mode          — 'on_water' | 'ergo'. Controls comparison direction.
 *   rollingWindow — number of samples in rolling avg (default 3, per §4).
 */
export default function WBTCrown({ liveValue, target, mode = 'on_water', rollingWindow = 3 }) {
  const samplesRef = useRef([]);
  const [active, setActive] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const fadeTimerRef = useRef(null);

  useEffect(() => {
    if (typeof liveValue !== 'number' || !Number.isFinite(liveValue)) return;
    const buf = samplesRef.current;
    buf.push(liveValue);
    if (buf.length > rollingWindow) buf.shift();

    if (!target || !Number.isFinite(target)) {
      if (active || fadingOut) startFadeOut();
      return;
    }
    if (buf.length < rollingWindow) return;

    const avg = buf.reduce((a, b) => a + b, 0) / buf.length;
    // On-water: higher speed wins (avg >= target).
    // Ergo: lower split wins (avg <= target).
    const meetsTarget = mode === 'ergo' ? avg <= target : avg >= target;

    if (meetsTarget) {
      if (fadeTimerRef.current) { clearTimeout(fadeTimerRef.current); fadeTimerRef.current = null; }
      setFadingOut(false);
      setActive(true);
    } else if (active && !fadingOut) {
      startFadeOut();
    }
  }, [liveValue, target, mode, rollingWindow]); // eslint-disable-line react-hooks/exhaustive-deps

  const startFadeOut = () => {
    setFadingOut(true);
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => {
      setActive(false);
      setFadingOut(false);
      fadeTimerRef.current = null;
    }, 2000);
  };

  useEffect(() => () => { if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current); }, []);

  if (!active && !fadingOut) return null;

  return (
    <span
      className="wbt-crown-slot"
      data-testid="wbt-crown"
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '2.1em',
        height: '2.1em',
        marginLeft: '0.5em',
        verticalAlign: 'middle',
        perspective: '400px',
        opacity: fadingOut ? 0 : 1,
        transition: 'opacity 2s ease-out',
        pointerEvents: 'none',
      }}
    >
      <Crown
        style={{
          width: '100%',
          height: '100%',
          color: '#F4C430',
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
          animation: 'wbt-crown-spin 2.4s linear infinite',
          transformOrigin: 'center center',
          transformStyle: 'preserve-3d',
        }}
        strokeWidth={2.25}
        fill="#F4C430"
      />
      <style>{`
        @keyframes wbt-crown-spin {
          0%   { transform: rotateY(0deg); }
          100% { transform: rotateY(360deg); }
        }
      `}</style>
    </span>
  );
}
